import { z } from 'zod';
import type { ProviderCredentialField } from './enrichment-catalog.js';
import type { DestinationType, NotifyChannel } from './agents.js';
import type { FeatureKey } from './features.js';

// In-repo connector registry behind the Integrations hub (modeled on
// ENRICHMENT_PROVIDERS). The manifest list is the catalog; a DB directory is later.

export const INTEGRATION_CATEGORIES = [
  'outbound',
  'notification',
  'developer',
  'crm',
] as const;
export type IntegrationCategory = (typeof INTEGRATION_CATEGORIES)[number];

// `available` = has a live connector (Connect affordance). `coming_soon` =
// browse-only in the catalog until its connector lands.
export const INTEGRATION_STATUSES = ['available', 'coming_soon'] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

// What Logspot sends; `timing` only when the delivery code fixes it, never an estimate.
export type IntegrationFlow = {
  label: string;
  timing?: string;
};

export type IntegrationManifest = {
  id: string;
  name: string;
  // One short sentence; anything longer goes in `details`.
  description: string;
  // Longer copy for the connect dialog.
  details?: string;
  // Logspot's help page, behind the overview's Setup Guide link; only where one is published.
  helpUrl?: string;
  flows?: IntegrationFlow[];
  // What teams do with it, as Title Case chip labels on the overview's Data Flow card.
  useCases?: string[];
  // Auth/security footer on the overview: only what the backend enforces.
  security?: string;
  category: IntegrationCategory;
  status: IntegrationStatus;
  authMode: 'settings' | 'oauth' | 'api_key';
  docsUrl?: string;
  // Feature this connector requires; absent = generally available. When set and
  // off, the connector is hidden even while the integrations hub is on.
  requiredFeature?: FeatureKey;
  // Launch gate: while off for an org, the connector shows as coming soon.
  launchFeature?: FeatureKey;
  // Gates new connections only: shows as coming soon while off, but existing connections still deliver.
  connectFeature?: FeatureKey;
  // How a connection of this integration is delivered. `notifyChannel` is the
  // discriminator when `deliversAs === 'notify'`.
  deliversAs: DestinationType;
  notifyChannel?: NotifyChannel;
  // Credential/config fields the connect dialog renders and the server validates.
  connectionFields: ProviderCredentialField[];
  // An org saves several of these, each named, and actions pick one by its name.
  namedConnections?: { one: string; many: string; listDescription: string };
  // CRM connectors: the CRM's own words, used in setup copy and run messages.
  crm?: CrmVocabulary;
};

// CRMs with a live connector; each has a backend adapter.
export const CRM_PROVIDER_IDS = ['hubspot', 'attio', 'salesforce'] as const;
export type CrmProviderId = (typeof CRM_PROVIDER_IDS)[number];
export const isCrmProviderId = (id: string): id is CrmProviderId =>
  (CRM_PROVIDER_IDS as readonly string[]).includes(id);

export type CrmVocabulary = {
  recordNoun: 'company' | 'account';
  fieldNoun: 'property' | 'attribute' | 'field';
  workspaceNoun: 'account' | 'workspace' | 'org';
  // Asks production or sandbox before connecting (Salesforce signs sandboxes in elsewhere).
  chooseEnvironment?: boolean;
  // Can also log each signal as an activity (a completed task) on the record.
  logsActivity?: boolean;
};

const SIGNAL_EVENT_RESULT_FLOWS: IntegrationFlow[] = [
  { label: 'Signals', timing: 'As they fire' },
  { label: 'Event Alerts', timing: 'As events arrive' },
  { label: 'Action Results' },
];

const CHANNEL_USE_CASES = [
  'Alert Reps on Intent',
  'Watch Key Events',
  'Share Action Results',
];

const CRM_USE_CASES = [
  'Prioritize Accounts',
  "Act While It's Fresh",
  'Brief Reps Before Calls',
];

export const INTEGRATION_MANIFESTS: IntegrationManifest[] = [
  {
    id: 'clay',
    helpUrl: 'https://www.logspot.io/docs/integrations/clay',
    authMode: 'settings',
    name: 'Clay',
    description: 'Send signals to a Clay table to enrich and run outbound.',
    details:
      "Clay enriches the account, finds the contact, and runs your outbound. Webhooks need Clay's Growth plan or higher; on Free or Launch, use Export CSV on the Signals page.",
    useCases: [
      'Enrich Hot Accounts',
      'Find the Right Contacts',
      'Run Outbound on Intent',
    ],
    flows: SIGNAL_EVENT_RESULT_FLOWS,
    security:
      "Your webhook URL is never shown again, and signals and event alerts respect each visitor's sharing consent.",
    category: 'outbound',
    status: 'available',
    requiredFeature: 'advancedIntegrations',
    docsUrl: 'https://university.clay.com/docs/webhook-integration-guide',
    deliversAs: 'clay',
    namedConnections: {
      one: 'Table',
      many: 'Tables',
      listDescription: 'Clay tables your actions can send signals to by name.',
    },
    connectionFields: [
      {
        key: 'webhookUrl',
        label: 'Clay Monitor Webhook URL',
        kind: 'secret',
        format: 'url',
        required: true,
        help: "In Clay: + Add → Webhooks → Monitor webhook, then copy the URL. Needs Clay's Growth plan or higher.",
      },
    ],
  },
  {
    id: 'slack',
    authMode: 'oauth',
    connectFeature: 'slackApp',
    name: 'Slack',
    description: 'Post signals and action results to a Slack channel.',
    details:
      'Installs the Logspot app in your Slack workspace, then posts to the channel you pick, with the account, signal strength, and evidence.',
    useCases: CHANNEL_USE_CASES,
    flows: SIGNAL_EVENT_RESULT_FLOWS,
    security:
      "Connects through Slack OAuth. Logspot's bot token is stored encrypted. Disconnecting deletes it and uninstalls the app from that workspace unless another Logspot organization still uses it.",
    category: 'notification',
    status: 'available',
    deliversAs: 'notify',
    notifyChannel: 'slack',
    // Not offered for new connections; kept so pasted-URL connections still deliver.
    connectionFields: [
      {
        key: 'slackWebhook',
        label: 'Slack Incoming Webhook URL',
        kind: 'secret',
        format: 'url',
        required: true,
      },
    ],
  },
  {
    id: 'discord',
    authMode: 'settings',
    name: 'Discord',
    description: 'Post signals and action results to a Discord channel.',
    details:
      'Posts to the channel behind your Discord webhook, with the account, signal strength, and evidence.',
    useCases: CHANNEL_USE_CASES,
    flows: SIGNAL_EVENT_RESULT_FLOWS,
    security: 'Your webhook URL is never shown again after you save it.',
    category: 'notification',
    status: 'available',
    deliversAs: 'notify',
    notifyChannel: 'discord',
    namedConnections: {
      one: 'Channel',
      many: 'Channels',
      listDescription: 'Discord channels your actions can post to by name.',
    },
    connectionFields: [
      {
        key: 'discordWebhook',
        label: 'Discord Webhook URL',
        kind: 'secret',
        format: 'url',
        required: true,
      },
    ],
  },
  {
    id: 'webhook',
    authMode: 'settings',
    name: 'Webhook',
    description: 'POST signals and action results to any HTTPS endpoint.',
    details:
      'Sends a JSON POST with the signal, the account, and its evidence.',
    useCases: [
      'Trigger Workflows',
      'Feed Internal Tools',
      'Log Signals Anywhere',
    ],
    flows: SIGNAL_EVENT_RESULT_FLOWS,
    security: 'Logspot only sends to public HTTPS endpoints.',
    category: 'developer',
    status: 'available',
    // Advanced tier (matches the historical Webhooks plan tier). The inline
    // per-action webhook destination is unaffected; this gates the hub connector.
    requiredFeature: 'advancedIntegrations',
    deliversAs: 'webhook',
    namedConnections: {
      one: 'Webhook',
      many: 'Webhooks',
      listDescription: 'Endpoints your actions can send to by name.',
    },
    connectionFields: [
      { key: 'url', label: 'Endpoint URL', kind: 'url', required: true },
    ],
  },
  {
    id: 'telegram',
    authMode: 'settings',
    name: 'Telegram',
    description: 'Send signals and action results to a Telegram chat.',
    details:
      'Uses the shared Logspot bot, so all you need is a chat ID. Message /start to the bot to get yours.',
    useCases: CHANNEL_USE_CASES,
    flows: SIGNAL_EVENT_RESULT_FLOWS,
    category: 'notification',
    status: 'available',
    docsUrl: 'https://t.me/logspot_bot',
    deliversAs: 'notify',
    notifyChannel: 'telegram',
    // Uses the shared Logspot Telegram bot, so only the chat id is needed (no
    // per-tenant secret). Message /start to the bot to get your chat id.
    connectionFields: [
      { key: 'chatId', label: 'Chat ID', kind: 'text', required: true },
    ],
  },
  ...[
    {
      id: 'hubspot',
      helpUrl: 'https://www.logspot.io/docs/integrations/hubspot',
      name: 'HubSpot',
      description: 'Send signals to matching HubSpot companies.',
      details:
        "When an action sends to HubSpot, Logspot updates the matching company with the Target Account's latest signal, its strength, a short summary, and a link back. Built-in signals are deduplicated to one per type per account each day, so HubSpot stays current without the noise.",
      useCases: CRM_USE_CASES,
      flows: [
        {
          label: 'Latest Signal & Strength',

          timing: 'As signals fire',
        },
        {
          label: 'Intent Summary & Logspot Link',

          timing: 'As signals fire',
        },
        { label: 'New Companies (Optional)' },
      ] satisfies IntegrationFlow[],
      security:
        "Connects through HubSpot OAuth. Disconnecting deletes Logspot's tokens, cancels queued updates, and uninstalls the app from the HubSpot account unless another Logspot organization still uses it.",
      status: 'available' as const,
      launchFeature: 'crmIntegrations' as const,
      crm: {
        recordNoun: 'company',
        fieldNoun: 'property',
        workspaceNoun: 'account',
      } satisfies CrmVocabulary,
    },
    {
      id: 'attio',
      helpUrl: 'https://www.logspot.io/docs/integrations/attio',
      name: 'Attio',
      description: 'Send signals to matching Attio companies.',
      details:
        "When an action sends to Attio, Logspot updates the matching company with the Target Account's latest signal, its strength, a short summary, and a link back. Built-in signals are deduplicated to one per type per account each day, so Attio stays current without the noise.",
      useCases: CRM_USE_CASES,
      flows: [
        { label: 'Latest Signal & Strength', timing: 'As signals fire' },
        { label: 'Intent Summary & Logspot Link', timing: 'As signals fire' },
        { label: 'New Companies (Optional)' },
      ] satisfies IntegrationFlow[],
      security:
        "Connects through Attio OAuth with PKCE. Disconnecting revokes Logspot's token and cancels queued updates.",
      status: 'available' as const,
      launchFeature: 'crmAttio' as const,
      crm: {
        recordNoun: 'company',
        fieldNoun: 'attribute',
        workspaceNoun: 'workspace',
      } satisfies CrmVocabulary,
    },
    {
      id: 'salesforce',
      helpUrl: 'https://www.logspot.io/docs/integrations/salesforce',
      name: 'Salesforce',
      description: 'Send signals to matching Salesforce accounts.',
      details:
        "When an action sends to Salesforce, Logspot updates the matching account with the Target Account's latest signal, its strength, a short summary, and a link back. The fields come from the Logspot package you install in Salesforce, and each signal can also be logged as a completed task on the account. Built-in signals are deduplicated to one per type per account each day, so Salesforce stays current without the noise.",
      useCases: CRM_USE_CASES,
      flows: [
        { label: 'Latest Signal & Strength', timing: 'As signals fire' },
        { label: 'Intent Summary & Logspot Link', timing: 'As signals fire' },
        { label: 'New Accounts (Optional)' },
      ] satisfies IntegrationFlow[],
      security:
        "Connects through Salesforce OAuth with PKCE. Disconnecting revokes Logspot's refresh token and cancels queued updates.",
      status: 'available' as const,
      launchFeature: 'crmSalesforce' as const,
      crm: {
        recordNoun: 'account',
        fieldNoun: 'field',
        workspaceNoun: 'org',
        chooseEnvironment: true,
        logsActivity: true,
      } satisfies CrmVocabulary,
    },
  ].map(
    (provider): IntegrationManifest => ({
      ...provider,
      category: 'crm',
      authMode: 'oauth',
      requiredFeature: 'advancedIntegrations',
      deliversAs: 'crm',
      connectionFields: [],
    }),
  ),
];

export const integrationReadinessSchema = z.strictObject({
  state: z.enum([
    'not_configured',
    'authorization_required',
    'setup_required',
    'ready',
    'paused',
    'error',
  ]),
  workspace_id: z.string().min(1).max(255).nullable(),
  checked_at: z.iso.datetime().nullable(),
  issue: z
    .enum([
      'app_not_configured',
      'authorization_expired',
      'missing_permissions',
      'property_setup_required',
      'workspace_changed',
      'provider_unavailable',
    ])
    .nullable(),
});
export type IntegrationReadiness = z.infer<typeof integrationReadinessSchema>;

// One connected instance of an integration for an org (the admin hub wire shape).
export type IntegrationConnectionView = {
  id: string;
  integration_id: string;
  name: string;
  status: string;
  config: Record<string, string>;
  last_connected_at: string | null;
  created_at: string;
  // The provider account the connection is bound to (HubSpot portal id).
  workspace?: string | null;
  // A readable name for that account, when the CRM gives one (Attio workspace name).
  workspace_label?: string | null;
  // Where the connection signed in, for CRMs that ask (Salesforce production or sandbox).
  environment?: CrmEnvironment | null;
  last_error?: string | null;
  readiness?: IntegrationReadiness;
  // The name the org gave this connection; set for integrations with `namedConnections`.
  label?: string | null;
};

// Request shapes for the admin hub endpoints; shared by the backend route and
// the admin client so the contract lives in one place.
export const connectIntegrationRequestSchema = z.object({
  integration_id: z.string().min(1),
  settings: z.record(z.string(), z.string()),
});
export type ConnectIntegrationRequest = z.infer<
  typeof connectIntegrationRequestSchema
>;

export const oauthCallbackRequestSchema = z.object({
  code: z.string().min(1).max(1024),
  state: z.string().min(1).max(256),
});
export type OAuthCallbackRequest = z.infer<typeof oauthCallbackRequestSchema>;

// Which login a CRM with separate test orgs (Salesforce sandboxes) sends the user to.
export const CRM_ENVIRONMENTS = ['production', 'sandbox'] as const;
export type CrmEnvironment = (typeof CRM_ENVIRONMENTS)[number];

export const crmAuthorizeQuerySchema = z.object({
  environment: z.enum(CRM_ENVIRONMENTS).optional(),
});

export const disconnectIntegrationRequestSchema = z.object({
  integration_id: z.string().min(1),
});
export type DisconnectIntegrationRequest = z.infer<
  typeof disconnectIntegrationRequestSchema
>;

// Adding or editing one named connection; actions pick it by this name. A blank
// secret setting on an edit keeps the saved one.
export const saveNamedConnectionRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(80),
  settings: z.record(z.string(), z.string()),
});
export type SaveNamedConnectionRequest = z.infer<
  typeof saveNamedConnectionRequestSchema
>;

export const hasNamedConnections = (integrationId: string): boolean =>
  !!getIntegration(integrationId)?.namedConnections;

export const getIntegration = (id: string): IntegrationManifest | undefined =>
  INTEGRATION_MANIFESTS.find((m) => m.id === id);

const LIVE_INTEGRATIONS: IntegrationManifest[] = INTEGRATION_MANIFESTS.filter(
  (m) => m.status === 'available',
);

export const isLiveIntegrationId = (id: string): boolean =>
  LIVE_INTEGRATIONS.some((m) => m.id === id);
